import React, { useState } from "react";
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import HistoryIcon from "@mui/icons-material/History";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "react-oidc-context";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getUserSessions, SessionListItem, deleteSession } from "../utils/backend-utils";

interface PastSessionsProps {
  onSessionSelect: (sessionId: string, goal: string) => void;
}

const PastSessions: React.FC<PastSessionsProps> = ({ onSessionSelect }) => {
  const theme = useTheme();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["userSessions"],
    queryFn: () => getUserSessions(auth),
    enabled: expanded && auth.isAuthenticated,
    staleTime: 30000, // 30 seconds
  });

  const deleteSessionMutation = useMutation({
    mutationFn: (sessionId: string) => deleteSession(sessionId, auth),
    onSuccess: () => {
      // Invalidate and refetch sessions list
      queryClient.invalidateQueries({ queryKey: ["userSessions"] });
    },
    onError: (err: Error) => {
      console.error("Failed to delete session:", err);
    },
  });

  const handleToggle = () => {
    setExpanded((prev) => !prev);
  };

  const handleDelete = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the session select
    deleteSessionMutation.mutate(sessionId);
  };

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case "COMPLETED":
      case "INTERROGATING_COMPLETE":
        return theme.palette.success.main;
      case "RUNNING":
      case "LISTENING":
        return theme.palette.info.main;
      case "NEEDS_INPUT":
        return theme.palette.warning.main;
      case "ERROR":
        return theme.palette.error.main;
      case "PLANNING":
        return theme.palette.info.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;

      return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "520px",
        mt: 2,
      }}
    >
      {/* Toggle Button */}
      <Box
        onClick={handleToggle}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          p: 1,
          cursor: "pointer",
          borderRadius: "8px",
          transition: "all 0.2s ease",
          "&:hover": {
            bgcolor: alpha(theme.palette.primary.main, 0.05),
          },
        }}
      >
        <HistoryIcon
          sx={{
            fontSize: 18,
            color: theme.palette.text.secondary,
          }}
        />
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: 500,
          }}
        >
          Past Sessions
        </Typography>
        <IconButton
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            ml: "auto",
          }}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {/* Expanded Content */}
      <Collapse in={expanded}>
        <Box
          sx={{
            mt: 1,
            p: 2,
            borderRadius: "12px",
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            maxHeight: "400px",
            overflowY: "auto",
          }}
        >
          {isLoading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 3,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          )}

          {error && (
            <Typography variant="body2" color="error" sx={{ textAlign: "center", py: 2 }}>
              Failed to load sessions. Please try again.
            </Typography>
          )}

          {data && data.sessions.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", py: 2 }}
            >
              No past sessions found
            </Typography>
          )}

          {data && data.sessions.length > 0 && (
            <List disablePadding>
              {data.sessions.map((session: SessionListItem, index: number) => (
                <ListItem
                  key={session.session_id}
                  sx={{
                    flexDirection: "column",
                    alignItems: "stretch",
                    gap: 1,
                    p: 2,
                    mb: index < data.sessions.length - 1 ? 1.5 : 0,
                    borderRadius: "8px",
                    bgcolor: theme.palette.background.default,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.02),
                      borderColor: alpha(theme.palette.primary.main, 0.2),
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 1,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: theme.palette.text.primary,
                            mb: 0.5,
                            lineHeight: 1.4,
                          }}
                        >
                          {session.goal}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(session.created_at)}
                        </Typography>
                      }
                    />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Chip
                        label={session.status}
                        size="small"
                        sx={{
                          bgcolor: alpha(getStatusColor(session.status), 0.1),
                          color: getStatusColor(session.status),
                          fontSize: "0.7rem",
                          height: "24px",
                          fontWeight: 500,
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={(e) => handleDelete(session.session_id, e)}
                        disabled={deleteSessionMutation.isPending}
                        sx={{
                          color: theme.palette.text.secondary,
                          "&:hover": {
                            color: theme.palette.error.main,
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                          },
                          transition: "all 0.2s ease",
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PlayArrowIcon />}
                    onClick={() => onSessionSelect(session.session_id, session.goal)}
                    sx={{
                      alignSelf: "flex-end",
                      textTransform: "none",
                      fontSize: "0.75rem",
                      py: 0.5,
                      px: 1.5,
                    }}
                  >
                    Continue
                  </Button>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default PastSessions;
